import { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import config from "config";
import useAuthUser from "./useAuthUser";
import emitter, { EVENTS } from "lib/emitter";
import providers from "lib/providers";

/**
 * Convert a URL-safe base64 string to a Uint8Array.
 * Required by PushManager.subscribe() for applicationServerKey.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map((char) => char.charCodeAt(0)));
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  return window.btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

const isDesktopApp = process.env.REACT_APP_DESKTOP === "true";
// Native (Capacitor) apps use Firebase push via useNativePush — never web push.
// (Also avoids awaiting navigator.serviceWorker.ready, which never resolves on
// native now that the SW is web-only.)
const isNativeApp = Capacitor.isNativePlatform();
const isSupported =
  !isDesktopApp && !isNativeApp && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

async function registerAndSubscribe(
  vapidKey: string,
  registrationRef: React.MutableRefObject<ServiceWorkerRegistration | null>
): Promise<boolean> {
  // Subscribe against the unified service worker registered in index.tsx
  // (it now owns push too). We must NOT register a second worker at "/" — that
  // would replace the precaching one and break the offline app shell.
  const reg = await navigator.serviceWorker.ready;
  registrationRef.current = reg;

  let subscription = await reg.pushManager.getSubscription();

  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
  }

  const p256dhKey = subscription.getKey("p256dh");
  const authKey = subscription.getKey("auth");
  if (!p256dhKey || !authKey) return false;

  try {
    await providers.PushNotificationsProvider.webPushNotificationsSubscribe({
      data: {
        endpoint: subscription.endpoint,
        p256dh: arrayBufferToBase64(p256dhKey),
        auth: arrayBufferToBase64(authKey),
      },
    });
  } catch (backendErr) {
    console.warn("[WebPush] Backend subscription failed:", backendErr);
  }

  return true;
}

export default function useWebPush() {
  const { user } = useAuthUser();
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const subscribedRef = useRef(false);
  // Guards the auto-subscribe effects so a single failed attempt doesn't
  // retry (and re-log) on every fresh `user` object emitted by auth events.
  const attemptedRef = useRef(false);
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );

  const hasVapidKey = !!config.vapidPublicKey;

  // Called by user gesture (button click on settings page)
  const subscribe = useCallback(async () => {
    if (!isSupported || !hasVapidKey || !user) return;
    if (subscribedRef.current) return;

    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return;

      const ok = await registerAndSubscribe(config.vapidPublicKey, registrationRef);
      if (ok) {
        subscribedRef.current = true;
        console.log("[WebPush] Subscribed successfully");
      }
    } catch (e) {
      console.error("[WebPush] Subscription failed:", e);
    }
  }, [hasVapidKey, user]);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = registrationRef.current;
      if (reg) {
        const subscription = await reg.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }
      subscribedRef.current = false;
      console.log("[WebPush] Unsubscribed");
    } catch (e) {
      console.error("[WebPush] Unsubscribe failed:", e);
    }
  }, []);

  // Auto-resubscribe if permission already granted (no prompt needed).
  // Depends on user?.id (not the user object) + attemptedRef so it runs once
  // per session instead of on every auth re-emit.
  useEffect(() => {
    if (!user || !isSupported || !hasVapidKey || subscribedRef.current || attemptedRef.current) return;
    if (Notification.permission !== "granted") return;

    attemptedRef.current = true;
    registerAndSubscribe(config.vapidPublicKey, registrationRef)
      .then((ok) => {
        if (ok) {
          subscribedRef.current = true;
          console.log("[WebPush] Re-subscribed (permission was already granted)");
        }
      })
      .catch((e) => console.error("[WebPush] Re-subscribe failed:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, hasVapidKey]);

  // Auto-prompt after login when permission not yet requested.
  // Works in Chrome/Edge/Firefox without a user gesture.
  // iOS Safari PWA requires a user gesture — falls back to the Settings page button.
  useEffect(() => {
    if (!user || !isSupported || !hasVapidKey || subscribedRef.current || attemptedRef.current) return;
    if (Notification.permission !== "default") return;

    const timer = setTimeout(() => {
      subscribe().catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Unsubscribe on logout
  useEffect(() => {
    const onLoginDataChange = (data: any) => {
      if (!data?.user) {
        attemptedRef.current = false;
        unsubscribe();
      }
    };
    emitter.on(EVENTS.SET_LOGIN_DATA, onLoginDataChange);
    return () => {
      emitter.off(EVENTS.SET_LOGIN_DATA, onLoginDataChange);
    };
  }, [unsubscribe]);

  return {
    isSupported: isSupported && hasVapidKey,
    permission,
    subscribe,
    unsubscribe,
  };
}
